import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';

import { useTranslation, Trans } from 'react-i18next';
import { createCompanyInvitation, readAxiosDetail, type CompanyInvitation } from './api_calls';

import { Icon } from '@liftango/ui-icons';
interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function InviteUserDialog({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation('company');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'company_admin'>('member');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Success state
  const [createdInvitation, setCreatedInvitation] = useState<CompanyInvitation | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEmail('');
    setRole('member');
    setExpiresInDays(7);
    setSaving(false);
    setError(null);
    setCreatedInvitation(null);
    setCopied(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!email.trim()) {
      setError(t('inviteDialog.emailRequired'));
      return;
    }

    setSaving(true);
    try {
      const invitation = await createCompanyInvitation({
        email: email.trim(),
        company_role: role,
        expires_in_days: expiresInDays,
      });
      setCreatedInvitation(invitation);
      onCreated();
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const inviteUrl = createdInvitation
    ? `${window.location.origin}/register?token=${createdInvitation.token}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text field
    }
  };

  // Success state — show the invite link
  if (createdInvitation) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('inviteDialog.createdTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <Trans
              i18nKey="inviteDialog.createdMessage"
              t={t}
              values={{ email: createdInvitation.email }}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('inviteDialog.createdHint', { date: new Date(createdInvitation.expires_at).toLocaleDateString() })}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={inviteUrl}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: 13 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => void handleCopy()}>
                    {copied ? <Icon name="check" size={20} /> : <Icon name="clipboard" size={20} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('inviteDialog.done')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Form state
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('inviteDialog.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          label={t('inviteDialog.emailLabel')}
          type="email"
          size="small"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <FormControl size="small" fullWidth>
          <InputLabel>{t('inviteDialog.roleLabel')}</InputLabel>
          <Select
            value={role}
            label={t('inviteDialog.roleLabel')}
            onChange={(e) => setRole(e.target.value as 'member' | 'company_admin')}
          >
            <MenuItem value="member">{t('inviteDialog.roleMember')}</MenuItem>
            <MenuItem value="company_admin">{t('inviteDialog.roleAdmin')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>{t('inviteDialog.expiresInLabel')}</InputLabel>
          <Select
            value={expiresInDays}
            label={t('inviteDialog.expiresInLabel')}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
          >
            <MenuItem value={7}>{t('inviteDialog.expires7Days')}</MenuItem>
            <MenuItem value={14}>{t('inviteDialog.expires14Days')}</MenuItem>
            <MenuItem value={30}>{t('inviteDialog.expires30Days')}</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t('inviteDialog.cancel')}</Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : t('inviteDialog.sendInvitation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
